/**
 * Pure occupancy split for “>2 guests in one criteria row” (see docs/room booking page/split occupancy.md).
 * No React — safe for unit tests and server reuse.
 */

export const SPLIT_STAY_DEFAULT_MAX_ROOMS = 5;

export type SplitOccupancyInput = {
  adults: number;
  children: number;
};

export type SplitOccupancyRow = { adults: number; children: number };

export type SplitOccupancyOk = { ok: true; rows: SplitOccupancyRow[] };

export type SplitOccupancyErr = {
  ok: false;
  reason: "max_rooms" | "insufficient_adults";
};

export type SplitOccupancyResult = SplitOccupancyOk | SplitOccupancyErr;

/**
 * Head-count partition for room slots: greedy **3, then 2, then 1**, with exceptions:
 * **T === 3 → [2, 1]**, **T === 4 → [2, 2]**.
 */
export function partitionStayHeadcount(total: number): number[] {
  if (total < 1) return [];
  if (total === 3) return [2, 1];
  if (total === 4) return [2, 2];
  const sizes: number[] = [];
  let remaining = total;
  while (remaining > 0) {
    if (remaining >= 3) {
      sizes.push(3);
      remaining -= 3;
    } else if (remaining === 2) {
      sizes.push(2);
      remaining -= 2;
    } else {
      sizes.push(1);
      remaining -= 1;
    }
  }
  return sizes;
}

/**
 * Split one criteria row into per-room occupancies.
 * Preconditions (caller): adults >= 1, children >= 0, adults + children > 2.
 *
 * Assignment rule (deterministic):
 * - One adult per output row minimum.
 * - Remaining adults, then remaining children, placed in **descending room size** (larger slots first);
 *   each row is capped by its partition slot size.
 */
export function splitOccupancyIntoRooms(
  input: SplitOccupancyInput,
  options?: { maxRooms?: number },
): SplitOccupancyResult {
  const { adults, children } = input;
  const maxRooms = options?.maxRooms ?? SPLIT_STAY_DEFAULT_MAX_ROOMS;
  const total = adults + children;

  const sizes = partitionStayHeadcount(total);
  if (sizes.length > maxRooms) {
    return { ok: false, reason: "max_rooms" };
  }
  if (adults < sizes.length) {
    return { ok: false, reason: "insufficient_adults" };
  }

  const r = sizes.length;
  const adultsPerRoom = Array.from({ length: r }, () => 1);
  const childrenPerRoom = Array.from({ length: r }, () => 0);
  let remA = adults - r;
  let remC = children;

  const order = sizes
    .map((size, index) => ({ size, index }))
    .sort((a, b) => b.size - a.size);

  for (const { size, index } of order) {
    let space = size - adultsPerRoom[index] - childrenPerRoom[index];
    const addA = Math.min(space, remA);
    adultsPerRoom[index] += addA;
    space -= addA;
    remA -= addA;
    const addC = Math.min(space, remC);
    childrenPerRoom[index] += addC;
    remC -= addC;
  }

  const rows: SplitOccupancyRow[] = sizes.map((_, i) => ({
    adults: adultsPerRoom[i]!,
    children: childrenPerRoom[i]!,
  }));

  return { ok: true, rows };
}
